import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { User, UserManager, UserManagerSettings } from "oidc-client-ts";
import { AppState } from "src/app/ngrx/root.reducer";
import { environment } from "src/environments/environment";
import { LogService } from "../../../../shared/services/log.service";
import { AuthActions } from "../../ngrx/auth.actions.types";

/** The shortest gap between the token renewals that user activity starts. */
export const ACTIVITY_RENEWAL_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: "root" })
export class AuthService {
  private homeUrl = "";
  private silentCallbackUrl = "";
  private sid: string = "";
  private readonly userManager: UserManager;
  private lastRenewalAt = 0;

  constructor(
    private store: Store<AppState>,
    private logService: LogService
  ) {
    const urlOrigin = window.location.origin;
    const urlLocale = window.location.href.split(window.origin)[1].split("/")[1];
    this.homeUrl = `${urlOrigin}${urlLocale.length === 5 ? "/" + urlLocale : ""}/home`;
    this.silentCallbackUrl = new URL("silent-callback.html", document.baseURI).href;
    this.userManager = new UserManager(this.settings());
    this.userManager.events.addUserLoaded((user) => {
      this.lastRenewalAt = Date.now();
      this.store.dispatch(
        AuthActions.TokenRefreshed({
          accessToken: user.access_token,
          expiresAt: user.expires_at ?? 0,
          profile: user.profile
        })
      );
    });
  }

  /**
   * Populate auth state at app start. Tries the locally-stored token first, then
   * falls back to a silent (prompt=none) sign-in against the shared Keycloak
   * session — so a user already signed into the realm (e.g. in another tab) is
   * authenticated here with no manual login click. Runs as an app
   * initializer, so it resolves before the router evaluates the auth guards
   * (which keeps the user on their deep-linked route instead of bouncing to
   * `/auth`). When there's no session, the silent sign-in fails fast with
   * `login_required` and the guard falls through to `/auth`.
   *
   * Skips the post-login redirect (`/<locale>/home?code=&state=`) and, inside
   * a silent sign-in iframe, Keycloak's error answer (`?error=&state=`);
   * HomeComponent completes both. A silent sign-in started inside that iframe
   * would delay its hand-off to the parent window past the parent's own timeout.
   */
  async bootstrapAuth(): Promise<void> {
    const params = new URLSearchParams(window.location.search);
    if (params.has("state") && (params.has("code") || (params.has("error") && window.self !== window.top))) {
      return;
    }

    let user: User | null = null;
    try {
      user = await this.userManager.getUser();
      if (!user || user.expired) {
        user = await this.userManager.signinSilent();
      }
    } catch (error) {
      this.logService.log(`[AuthService] bootstrapAuth() silent sign-in unavailable: ${error}`);
      user = null;
    }

    if (user && !user.expired) {
      this.sid = user.profile.sid ?? "";
      this.store.dispatch(
        AuthActions.Login({
          accessToken: user.access_token,
          expiresAt: user.expires_at ?? 0,
          profile: user.profile,
          autoLogin: true
        })
      );
    }
  }

  async login() {
    this.logService.log("[AuthService] login() Dispatching request to oidcUserManager.signinRedirect()");
    await this.userManager.signinRedirect();
  }

  async logout() {
    this.logService.log("[AuthService] logout() Dispatching request to oidcUserManager.signoutRedirect()");
    await this.userManager.signoutRedirect({
      post_logout_redirect_uri: this.homeUrl,
      extraQueryParams: { logout_hint: this.sid }
    });
  }

  /**
   * Finish a silent sign-in / renew from inside a hidden iframe that
   * oidc-client-ts opened at the redirect_uri, which it does when a tab's
   * settings name no silent_redirect_uri. This posts the result back to the
   * parent window (which performs the token exchange); without it the parent's
   * signinSilent() waits out its timeout. Only call this when running framed
   * (see HomeComponent).
   */
  async completeSilentLogin() {
    this.logService.log("[AuthService] completeSilentLogin() => signinSilentCallback()");
    await this.userManager.signinSilentCallback();
  }

  /**
   * Renews the tokens when the last renewal is more than {@link ACTIVITY_RENEWAL_INTERVAL_MS} old. A failed
   * attempt counts too, so it is retried no sooner than that. Call on user input: a renewal resets Keycloak's
   * session idle timeout and API calls do not, so an active user would otherwise be signed out once that
   * timeout passes.
   */
  async renewTokenIfStale(): Promise<void> {
    const now = Date.now();
    if (now - this.lastRenewalAt <= ACTIVITY_RENEWAL_INTERVAL_MS) {
      return;
    }

    this.lastRenewalAt = now;
    try {
      await this.userManager.signinSilent();
    } catch (error) {
      this.logService.log(`[AuthService] renewTokenIfStale() silent renew failed: ${error}`);
    }
  }

  async completeLogin() {
    this.logService.log("[AuthService] completeLogin() Dispatching request to oidcUserManager.signinRedirectCallback()");
    const user = await this.userManager.signinRedirectCallback();
    return {
      accessToken: user.access_token,
      expiresAt: user.expires_at ?? 0,
      profile: user.profile,
      autoLogin: false
    };
  }

  private settings(): UserManagerSettings {
    return {
      authority: environment.authority,
      redirect_uri: this.homeUrl,
      silent_redirect_uri: this.silentCallbackUrl,
      post_logout_redirect_uri: this.homeUrl,
      client_id: environment.client_id,
      response_type: "code",
      scope: "openid profile",
      automaticSilentRenew: true,
      includeIdTokenInSilentRenew: true,
      loadUserInfo: true,
      accessTokenExpiringNotificationTimeInSeconds: 30,
      revokeTokensOnSignout: true,
      monitorSession: false
    };
  }
}
